import { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { TID } from "@/constants/tid";
import GoogleAuthButton from "@/components/GoogleAuthButton";
import PasswordInput from "@/components/PasswordInput";
import { api, formatApiError } from "@/lib/api";

export default function Login() {
  const { login, setUser } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const next = new URLSearchParams(loc.search).get("next");
  const safeNext = next && next.startsWith("/") ? next : null;
  const authError = loc.state?.authError;
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState(authError || "");
  const [busy, setBusy] = useState(false);
  const [challenge, setChallenge] = useState(null);
  const [code, setCode] = useState("");
  const [emailSent, setEmailSent] = useState(false);

  const goIn = (u) => {
    const dest = u.role === "seller" ? "/seller" : u.role === "admin" ? "/admin" : "/discover";
    nav(safeNext || dest);
  };

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setBusy(true);
    try {
      const u = await login(email, password);
      if (u?.twofa_required) { setChallenge(u.challenge); return; }
      goIn(u);
    } catch (e2) {
      setErr(e2.message);
    } finally {
      setBusy(false);
    }
  };

  const verify2fa = async (e) => {
    e.preventDefault();
    setErr("");
    setBusy(true);
    try {
      const { data } = await api.post("/auth/2fa/verify", { challenge, code });
      setUser(data);
      goIn(data);
    } catch (e2) {
      setErr(formatApiError(e2.response?.data?.detail) || "Invalid code");
    } finally {
      setBusy(false);
    }
  };

  const emailCode = async () => {
    setErr("");
    try {
      await api.post("/auth/2fa/email-code", { challenge });
      setEmailSent(true);
    } catch (e2) {
      setErr(formatApiError(e2.response?.data?.detail) || "Could not send code");
    }
  };

  if (challenge) {
    return (
      <div className="min-h-[calc(100vh-4rem)] flex items-center justify-center p-8">
        <form onSubmit={verify2fa} className="w-full max-w-sm" data-testid="twofa-step">
          <h1 className="font-black tracking-tight text-3xl">Two-step verification 🔐</h1>
          <p className="text-neutral-400 mt-2 text-sm">
            Enter the 6-digit code from your <span className="text-white">authenticator app</span>
            {emailSent ? " or the code we just emailed you" : ""}, or a backup code.
          </p>
          <Input
            autoFocus
            required
            data-testid="twofa-code-input"
            value={code}
            onChange={(e) => setCode(e.target.value)}
            className="mt-6 bg-[#121212] border-[#27272A] text-white text-center font-mono text-xl tracking-[0.4em]"
            placeholder="••••••"
            maxLength={12}
          />
          {err && (
            <div data-testid="twofa-error" className="mt-4 text-sm text-[#FF3B30] border border-[#FF3B30]/40 bg-[#FF3B30]/10 rounded-md px-3 py-2">{err}</div>
          )}
          <Button type="submit" disabled={busy || code.length < 6} data-testid="twofa-verify-btn"
            className="mt-5 w-full bg-white text-black hover:bg-neutral-200 rounded-full h-11 font-semibold">
            {busy ? "Verifying…" : "Verify & sign in"}
          </Button>
          <button type="button" onClick={emailCode} data-testid="twofa-email-btn"
            className="mt-4 w-full text-xs text-[#007AFF] hover:underline">
            {emailSent ? "Resend email code" : "No app access? Email me a code instead"}
          </button>
          <button type="button" onClick={() => { setChallenge(null); setCode(""); setErr(""); }}
            className="mt-2 w-full text-xs text-neutral-500 hover:text-white">
            ← Back to sign in
          </button>
        </form>
      </div>
    );
  }

  return (
    <div className="min-h-[calc(100vh-4rem)] grid lg:grid-cols-2">
      <div className="hidden lg:block relative bg-[#0A0A0A] border-r border-white/5">
        <div
          className="absolute inset-0 opacity-[0.06]"
          style={{
            backgroundImage:
              "linear-gradient(rgba(255,255,255,0.7) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.7) 1px, transparent 1px)",
            backgroundSize: "48px 48px",
          }}
        />
        <div className="relative h-full flex flex-col justify-end p-16">
          <div className="text-[10px] uppercase tracking-widest text-[#007AFF]">Vismarks</div>
          <h2 className="mt-4 font-black tracking-tight text-4xl leading-tight max-w-md">
            Every listing stays live until it sells.
          </h2>
          <p className="mt-4 text-neutral-400 max-w-md">
            Sign in to publish, monitor and match your inventory to live demand.
          </p>
        </div>
      </div>

      <div className="flex items-center justify-center p-8 lg:p-16">
        <form onSubmit={submit} className="w-full max-w-sm">
          <h1 className="font-black tracking-tight text-3xl">Welcome back</h1>
          <p className="text-neutral-400 mt-2 text-sm">Sign in to your Vismarks account.</p>

          <div className="mt-8 space-y-5">
            <div>
              <Label className="text-xs uppercase tracking-widest text-neutral-400">Email</Label>
              <Input
                type="email"
                required
                data-testid={TID.loginEmail}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="mt-2 bg-[#121212] border-[#27272A] text-white placeholder:text-neutral-600"
                placeholder="you@company.com"
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-neutral-400">Password</Label>
              <PasswordInput
                required
                data-testid={TID.loginPassword}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="mt-2 bg-[#121212] border-[#27272A] text-white placeholder:text-neutral-600"
                placeholder="••••••••"
              />
            </div>

            {err && (
              <div
                data-testid={TID.loginError}
                className="text-sm text-[#FF3B30] border border-[#FF3B30]/40 bg-[#FF3B30]/10 rounded-md px-3 py-2"
              >
                {err}
              </div>
            )}

            <Button
              type="submit"
              disabled={busy}
              data-testid={TID.loginSubmit}
              className="w-full bg-white text-black hover:bg-neutral-200 rounded-full h-11 font-semibold"
            >
              {busy ? "Signing in…" : "Sign in"}
            </Button>

            <div className="text-center">
              <Link to="/forgot-password" data-testid="forgot-password-link" className="text-xs text-neutral-400 hover:text-[#007AFF]">
                Forgot password?
              </Link>
            </div>

            <div className="relative py-1">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-white/10" />
              </div>
              <div className="relative flex justify-center">
                <span className="bg-[#0A0A0A] px-3 text-[10px] uppercase tracking-widest text-neutral-500">or</span>
              </div>
            </div>

            <GoogleAuthButton testId="login-google-btn" />

            <div className="text-sm text-neutral-500 text-center">
              New here?{" "}
              <Link to="/register" className="text-white hover:underline">
                Create an account
              </Link>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}
